import { runInAction } from "mobx";
import { atom } from "../atom";
import {
  ActionType,
  Pane,
  PaneCoreType,
  PaneInput,
  PaneType,
  PaneViewMode,
  Position,
  Rect,
} from "../Types";
import { generatePaneId, generatePaneInputId } from "../Utils";
import { canvasDispatch } from "./CanvasDispatch";
import { activePaneStates, selectedPaneIdsAtom } from "./CanvasState";

type ClipboardPaneInput = Omit<PaneInput, "id">;
type TransformClipboardPaneInput<T> = T extends PaneInput[]
  ? ClipboardPaneInput[]
  : T;
type MapTransformClipboardPaneInput<T> = {
  [K in keyof T]: TransformClipboardPaneInput<T[K]>;
};
export type ClipboardPane = {
  z: number;
  rect: Rect;
  name?: string;
  flags?: number;
} & MapTransformClipboardPaneInput<PaneCoreType>;
export type ClipboardSelection = {
  id: number;
  panes: ClipboardPane[];
};

export const clipboardSelectionAtom = atom<ClipboardSelection[]>([]);

export function getPanesTopLeft(panes: Pane[]): Position {
  let rv: Position = [Infinity, Infinity];
  panes.forEach((p) => {
    const paneRect = activePaneStates.get(p.id)!.rect.get();
    rv[0] = Math.min(paneRect[0], rv[0]);
    rv[1] = Math.min(paneRect[1], rv[1]);
  });
  return rv;
}

export function normalizePanesForClipboard(
  panes: Pane[],
  isDuplicate: boolean
): ClipboardPane[] {
  const topLeft = getPanesTopLeft(panes);
  const paneIds = panes.map((p) => p.id);
  const zIndexMap: Map<string, number> = new Map();
  [...panes]
    .sort(
      (a, b) =>
        activePaneStates.get(a.id)!.zIndex.get() -
        activePaneStates.get(b.id)!.zIndex.get()
    )
    .forEach((p, i) => {
      zIndexMap.set(p.id, i + 1);
    });

  function mapInputSource(
    inputPaneId: string,
    outputIndex: number
  ): [paneId: string, outputIndex: number] | undefined {
    if (isDuplicate) {
      // If duplicate, we can reference a pane outside the selection. If the
      // referenced pane is also in the copy selection, the reference will be
      // a `#` followed by the pane index.
      return [
        paneIds.includes(inputPaneId)
          ? `#${paneIds.indexOf(inputPaneId)}`
          : inputPaneId,
        outputIndex,
      ];
    } else {
      return paneIds.includes(inputPaneId)
        ? [`#${paneIds.indexOf(inputPaneId)}`, outputIndex]
        : undefined;
    }
  }

  return panes.map((p) => {
    const activePaneState = activePaneStates.get(p.id)!;
    let paneRect =
      activePaneState.viewMode.get() === PaneViewMode.Collapsed
        ? p.rect
        : activePaneState.rect.get();
    const rv: ClipboardPane = {
      ...p,
      rect: [
        paneRect[0] - topLeft[0],
        paneRect[1] - topLeft[1],
        paneRect[2],
        paneRect[3],
      ],
      z: zIndexMap.get(p.id)!,
    };
    if ("inputs" in p && rv.type === PaneType.Evaluate) {
      rv.inputs = p.inputs.map((paneInput: PaneInput): ClipboardPaneInput => {
        if (paneInput.source === undefined) {
          return {
            name: paneInput.name,
          };
        }
        const inputPaneSource = paneInput.source;
        return {
          name: paneInput.name,
          source: mapInputSource(inputPaneSource[0], inputPaneSource[1]),
        };
      });
    }
    if (rv.type === PaneType.EvaluateTemplate) {
      rv.templateInputSources = Object.fromEntries(
        Object.entries(rv.templateInputSources).flatMap(
          ([inputId, inputSource]) => {
            const newInputSource = mapInputSource(
              inputSource[0],
              inputSource[1]
            );
            if (newInputSource === undefined) {
              return [];
            }
            return [[inputId, newInputSource]];
          }
        )
      );
    }
    if (
      rv.type === PaneType.Evaluate &&
      rv.renderOutput?.[0] === "custom" &&
      rv.renderOutput[1] !== undefined
    ) {
      const [inputPaneId, outputIndex] = rv.renderOutput[1];
      const inputSource = mapInputSource(inputPaneId, outputIndex);
      rv.renderOutput =
        inputSource !== undefined ? ["custom", inputSource] : ["custom"];
    }
    if (
      rv.type === PaneType.Evaluate &&
      rv.renderOutput?.[0] === "react" &&
      rv.renderOutput[1]?.ReactDOM !== undefined
    ) {
      const [inputPaneId, outputIndex] = rv.renderOutput[1].ReactDOM;
      const inputSource = mapInputSource(inputPaneId, outputIndex);
      rv.renderOutput =
        inputSource !== undefined
          ? ["react", { ReactDOM: inputSource }]
          : ["react"];
    }
    if (rv.type === PaneType.LLM && rv.inputSource !== undefined) {
      const [inputPaneId, outputIndex] = rv.inputSource;
      const inputSource = mapInputSource(inputPaneId, outputIndex);
      rv.inputSource = inputSource;
    }
    if (rv.type === PaneType.Function) {
      rv.paneIds = rv.paneIds.map((paneId) =>
        paneIds.includes(paneId) ? `#${paneIds.indexOf(paneId)}` : paneId
      );
      if (rv.inputPaneId !== undefined && paneIds.includes(rv.inputPaneId)) {
        rv.inputPaneId = `#${paneIds.indexOf(rv.inputPaneId)}`;
      }
      if (rv.outputPaneId !== undefined && paneIds.includes(rv.outputPaneId)) {
        rv.outputPaneId = `#${paneIds.indexOf(rv.outputPaneId)}`;
      }
    }
    // @ts-ignore
    delete rv.id;
    return rv;
  });
}

export function addSelectionToClipboard(panes: Pane[]) {
  const paneIds = selectedPaneIdsAtom.value;
  const selections = clipboardSelectionAtom.value;
  const newSelectionId =
    selections.length > 0 ? Math.max(...selections.map((s) => s.id)) + 1 : 1;
  const selectedPanes = panes.filter((p) => paneIds.includes(p.id));
  if (selectedPanes.length === 0) {
    return;
  }
  const normalizedPanes = normalizePanesForClipboard(selectedPanes, false);
  runInAction(() => {
    clipboardSelectionAtom.value = [
      {
        id: newSelectionId,
        panes: normalizedPanes,
      },
      ...selections,
    ];
  });
  return normalizedPanes;
}

export function pasteClipboard(
  clipboard: ClipboardSelection,
  clipboardOffset: Position
) {
  const newPaneIds = clipboard.panes.map((p) => generatePaneId());
  function mapInputSource(paneId: string) {
    return paneId.startsWith("#")
      ? newPaneIds[parseInt(paneId.slice(1))]
      : paneId;
  }
  canvasDispatch([
    ActionType.PastePanes,
    {
      panes: clipboard.panes.map((p, i) => {
        // @ts-ignore: this is a ClipboardPane turning into a Pane
        const newPane: Pane = {
          ...p,
          id: newPaneIds[i],
          rect: [
            p.rect[0] + clipboardOffset[0],
            p.rect[1] + clipboardOffset[1],
            p.rect[2],
            p.rect[3],
          ] as Rect,
        };
        if ("inputs" in p && newPane.type === PaneType.Evaluate) {
          newPane.inputs = p.inputs.map((input): PaneInput => {
            if (input.source === undefined) {
              return {
                id: generatePaneInputId(),
                name: input.name,
              };
            }
            const [inputPaneId, outputIndex] = input.source;
            return {
              id: generatePaneInputId(),
              name: input.name,
              source: [mapInputSource(inputPaneId), outputIndex],
            };
          });
        }
        if (
          "templateInputSources" in p &&
          newPane.type === PaneType.EvaluateTemplate
        ) {
          newPane.templateInputSources = Object.fromEntries(
            Object.entries(p.templateInputSources).flatMap(
              ([inputId, source]) => {
                if (source === undefined) {
                  return [];
                }
                const [inputPaneId, outputIndex] = source;
                return [[inputId, [mapInputSource(inputPaneId), outputIndex]]];
              }
            )
          );
        }
        if (
          newPane.type === PaneType.Evaluate &&
          newPane.renderOutput?.[0] === "custom" &&
          newPane.renderOutput[1] !== undefined
        ) {
          const [inputPaneId, outputIndex] = newPane.renderOutput[1];
          newPane.renderOutput[1] = [mapInputSource(inputPaneId), outputIndex];
        }
        if (
          newPane.type === PaneType.Evaluate &&
          newPane.renderOutput?.[0] === "react" &&
          newPane.renderOutput[1]?.ReactDOM !== undefined
        ) {
          const [inputPaneId, outputIndex] = newPane.renderOutput[1].ReactDOM;
          newPane.renderOutput[1].ReactDOM = [
            mapInputSource(inputPaneId),
            outputIndex,
          ];
        }
        if (
          newPane.type === PaneType.LLM &&
          newPane.inputSource !== undefined
        ) {
          const [inputPaneId, outputIndex] = newPane.inputSource;
          newPane.inputSource = [mapInputSource(inputPaneId), outputIndex];
        }
        if (newPane.type === PaneType.Function) {
          newPane.paneIds = newPane.paneIds.map(mapInputSource);
          if (newPane.inputPaneId !== undefined) {
            newPane.inputPaneId = mapInputSource(newPane.inputPaneId);
          }
          if (newPane.outputPaneId !== undefined) {
            newPane.outputPaneId = mapInputSource(newPane.outputPaneId);
          }
        }
        return newPane;
      }),
    },
  ]);
}

export function removeClipboardSelection(selectionId: number) {
  runInAction(() => {
    clipboardSelectionAtom.value = clipboardSelectionAtom.value.filter(
      (s) => s.id !== selectionId
    );
  });
}
